import React, { useContext } from "react";
import { graphql, useStaticQuery } from "gatsby";
import Container from "./container";
import { Logo } from "../icon/logo";
import { LinkedIn } from "../icon/linkedin";
import { Twitter } from "../icon/twitter";
import {
  ATTRIBUTION_PARAM,
  DEMO_URL,
  LogoLink,
} from "../sections/snowflake/navigation";
import { Slack } from "../icon/slack";
import { pageDocPath, trackPath } from "../../../helpers";
import { SiteContext } from "./site-context";
import { MenuLink } from "../sections/snowflake/navigation";
import { YouTube } from "../icon/youtube";
import {
  JOIN_SLACK_PATH,
  LINKEDIN_URL,
  YOUTUBE_URL,
} from "../sections/snowflake/channels-buttons";
import { cn, isThinPage, mapEdgesToNodes } from "../../lib/helpers";
import {
  APPLE_SHOW_URL,
  SPOTIFY_SHOW_URL,
} from "../sections/snowflake/podcast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpotify } from "@fortawesome/free-brands-svg-icons";
import { faPodcast } from "@fortawesome/free-solid-svg-icons";

// Same Sanity menu document used by the global nav.
const query = graphql`
  query FooterMenuQuery {
    menus: allSanityMenu(
      filter: { _id: { eq: "0ae244a8-0e78-482c-bb63-0422b316fcce" } }
    ) {
      edges {
        node {
          items {
            _key
            title
            reference {
              ... on SanityPage {
                _type
                title
                slug {
                  current
                }
              }
              ... on SanityOffering {
                _type
                title
                slug {
                  current
                }
              }
            }
            href
            children {
              _key
              title
              reference {
                ... on SanityPage {
                  _type
                  title
                  slug {
                    current
                  }
                }
                ... on SanityOffering {
                  _type
                  title
                  slug {
                    current
                  }
                }
              }
              href
              children {
                _key
                title
                reference {
                  ... on SanityPage {
                    _type
                    title
                    slug {
                      current
                    }
                  }
                  ... on SanityOffering {
                    _type
                    title
                    slug {
                      current
                    }
                  }
                }
                href
              }
            }
          }
        }
      }
    }
  }
`;

const makeUrl = (item) =>
  item.href ? item.href : item.reference ? pageDocPath(item.reference) : null;

const makeProductMenu = (items) => {
  const product = items?.find(
    (item) => (item.title || item.reference?.title) === "Product"
  );

  // Product children are group headers (e.g. "Common Room", "Zoom"); their
  // own children are the actual product links, indented beneath the group.
  return {
    label: "Product",
    links: (product?.children || []).flatMap((group) => {
      const groupLink = {
        label: group.title || group.reference?.title,
        to: makeUrl(group),
      };
      const groupLinks = (group.children || []).map((link) => ({
        label: link.title || link.reference?.title,
        to: makeUrl(link),
        indent: true,
      }));

      return [groupLink, ...groupLinks];
    }),
  };
};

const makeMenus = (slug, productMenu) => {
  return [
    productMenu,
    {
      label: "Solutions",
      links: [
        {
          label: "Sales (AEs)",
          to: "/solutions/sales/",
        },
        {
          label: "Demand Generation",
          to: "/solutions/demand-generation/",
        },
        {
          label: "Sales Development",
          to: "/solutions/sales-development/",
        },
        {
          label: "Revenue Operations",
          to: "/solutions/revops/",
        },
        {
          label: "Account-based",
          to: "/solutions/abm/",
        },
        {
          label: "Open Source",
          to: "/solutions/open-source/",
        },
      ],
    },
    {
      label: "Resources",
      links: [
        {
          label: "Resource Hub",
          to: "/resources/",
        },
        {
          label: "Blog",
          to: "/blog/",
        },
        {
          label: "Docs",
          href: "/docs/",
        },
        {
          label: "Academy",
          href: "/academy/",
        },
        {
          label: "Events & Webinars",
          href: "/events/",
        },
        {
          label: "Playbooks",
          to: "/resources/playbooks/",
        },
        {
          label: "Customers",
          to: "/customers/",
        },
        {
          label: "Signal Guides",
          to: "/resources/signals/",
        },
        {
          label: "Community",
          to: "/community/",
        },
        {
          label: "Glossary",
          href: "/glossary/",
        },
        {
          label: "Developers",
          to: "/developers/",
        },
        {
          label: "API Docs",
          href: "https://api.commonroom.io/docs/api-v2.html",
          attributes: { target: "_blank" },
        },
        {
          label: "Pricing",
          to: "/pricing/",
          footerOnly: true,
        },
      ],
    },
    {
      label: "Company",
      links: [
        {
          label: "Careers",
          to: "/careers/",
        },
        {
          label: "Request demo",
          to: `${DEMO_URL}?${ATTRIBUTION_PARAM}=footer--${slug}`,
        },
        {
          label: "About",
          to: "/about/",
        },
        {
          label: "Security",
          to: "/security/",
        },
        {
          label: "Privacy",
          to: "/privacy-policy/",
        },
        {
          label: "Terms",
          to: "/terms-of-use/",
        },        // {
        //   label: "Say hello",
        //   to: "mailto:hello@commonroom.io",
        //   attributes: {
        //     target: "_blank",
        //     rel: "noreferrer",
        //   },
        // },
      ],
    },
  ];
};

const socialLinks = [
  {
    label: "LinkedIn",
    href: LINKEDIN_URL,
    icon: <LinkedIn />,
    color: "brand-linkedin",
  },
  {
    label: "YouTube",
    href: YOUTUBE_URL,
    icon: <YouTube />,
    color: "brand-youtube",
  },
  {
    label: "Slack",
    href: JOIN_SLACK_PATH,
    icon: <Slack size={20} color="currentColor" />,
    color: "brand-slack2",
  },
  {
    label: "Spotify",
    href: SPOTIFY_SHOW_URL,
    icon: <FontAwesomeIcon icon={faSpotify} size="xl" />,
    color: "brand-spotify",
  },
  {
    label: "Apple Podcasts",
    href: APPLE_SHOW_URL,
    icon: <FontAwesomeIcon icon={faPodcast} size="xl" />,
    color: "brand-applepodcasts",
  },
];

export const Footer = () => {
  const site = useContext(SiteContext);
  const data = useStaticQuery(query);
  const menus = mapEdgesToNodes(data.menus);
  const productMenu = makeProductMenu(menus[0]?.items);
  const path = trackPath(site?.doc);
  const slimLayout = isThinPage(site);
  const transparent = site?.doc?.backgroundColor === "gridBlack";

  return (
    <footer className={cn(
      "pt-12 pb-20 md:pb-24 text-white relative overflow-hidden",
      transparent ? null : "bg-gray-1000"
    )}>
      <Container
        maxWidth={slimLayout ? "max-w-screen-lg" : null}
        className="relative"
      >
        <div className="grid grid-cols-1 md:grid-cols-1/3-2/3 gap-12 md:gap-y-0">
          <div>
            <div className="text-light-50 group hover:text-white transition-colors duration-500">
              <LogoLink>
                <Logo className="h-8" isDarkBg={true} />
              </LogoLink>
              <p className="leading-tight group-hover:text-light-80 transition-colors duration-500 font-medium mt-1 ml-8 w-64">
                {site.token("slogan")}
              </p>
            </div>
          </div>

          {site.hideNav ? null : (
            <div className="grid grid-cols grid-cols-2 sm:grid-cols-4 gap-4 gap-y-12 ml-8 sm:ml-11 md:ml-0">
              {makeMenus(path, productMenu).map((menu, i) => (
                <div key={i} className="text-sm">
                  {i !== 0 && (
                    <div className="mb-1.5 text-light-80">
                      {menu.label}
                    </div>
                  )}
                  <ul key={i}>
                    {menu.links.map((link, n) =>
                      link.skipFooter ? null : link.to || link.href ? (
                        <li key={n} className={cn("mb-2", link.indent ? "ml-0" : null)}>
                          <MenuLink
                            link={link}
                            linkClass="block text-light-50 hover:text-white"
                            showIndent={true}
                          />
                        </li>
                      ) : link.label ? (
                        <li key={n} className="mb-2 text-light-80">
                          {link.label}
                        </li>
                      ) : null
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
          <div
            className={cn(
              "text-sm text-light-50 md:order-2 flex ml-8 sm:ml-11 md:ml-0",
              site.hideNav ? "justify-self-end" : null
            )}
          >
            {socialLinks.map((link, i) => (
              <a
                key={i}
                href={link.href}
                className={cn(
                  "mr-4 flex items-center hover:text-white transform hover:scale-125 transition-all duration-300",
                  link.color ? `hover:text-${link.color}` : null
                )}
                target={link.href !== JOIN_SLACK_PATH ? "_blank" : null}
                rel="noreferrer"
                {...link.attributes}
                aria-label={link.label}
              >
                {link.icon}
              </a>
            ))}
          </div>
        </div>
      </Container>
    </footer>
  );
};
